import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/api';
import { Schedule } from '../types/schedule';
import { scheduleApi } from '../mocks/scheduleApi';
import { CLASSIFICATIONS } from '../types/schedule';
import { BidSource } from '../types/bid';
import { BidSummaryPanel } from '../components/BidSummaryPanel';
import { BidBookmarkList } from '../components/BidBookmarkList';
import { SidePanel } from '../components/SidePanel';
import { ConfirmButton } from '../components/ConfirmButton';
import { ErrorBanner } from '../components/ErrorBanner';
import { ScheduleFormModal } from '../components/ScheduleFormModal';
import { KeywordFields } from '../components/KeywordFields';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  BadgeCheck,
  Bookmark,
  Loader2,
  ChevronDown,
} from 'lucide-react';

type SortBy = 'bidClseDt' | 'presmptPrce';

/** 정렬 — 시안의 세 가지 */
const SORTS: {
  label: string;
  sortBy: SortBy;
  sortOrder: 'asc' | 'desc';
}[] = [
  { label: '마감 임박순', sortBy: 'bidClseDt', sortOrder: 'asc' },
  { label: '추정가격순', sortBy: 'presmptPrce', sortOrder: 'desc' },
];

/** 나라장터는 첨부를 1~10 번 슬롯으로 준다 (2026-09-26 실측). 예전엔 3까지만 읽었다 */
const ATTACH_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

interface BidItem {
  bidNtceNo: string;
  /** 공고 차수. 정정할 때마다 올라간다 — BE 가 최신 것만 내려준다 */
  bidNtceOrd: string;
  bidNtceNm: string;
  ntceInsttNm: string;
  dminsttNm: string;
  bidNtceDt: string;
  bidClseDt: string;
  /** 개찰일시. 협상에 의한 계약은 마감 대신 이쪽만 있다 */
  opengDt: string;
  presmptPrce: string;
  bidNtceDtlUrl: string;
  bidNtceUrl: string;
  ntceKindNm: string;
  /** 물품분류 대분류. 용역에만 있다 */
  pubPrcrmntLrgClsfcNm?: string;
  /** 계약방법. 응답 필드명은 cntrctCnclsMthdNm 이다 (2026-09-26 실측) */
  cntrctCnclsMthdNm: string;
  bidMethdNm: string;
  asignBdgtAmt: string;
  /** 대분류·중분류. **용역에만 값이 있다** — 물품·공사는 빈 값이다 */
  pubPrcrmntMidClsfcNm?: string;
  /** 일반용역 · 기술용역 */
  srvceDivNm?: string;
  /** 물품에만 있다 */
  dtilPrdctClsfcNoNm?: string;
  prdctQty?: string;
  prdctUnit?: string;
  /** 수집 때 붙인 면허제한. 물품·공사엔 대분류가 없어 이게 실질적인 분류다 */
  licenses?: {
    lcnsLmtNm?: string;
    permsnIndstrytyList?: string | null;
    lmtGrpNo?: string;
  }[];
  ntceSpecDocUrl1?: string;
  ntceSpecDocUrl2?: string;
  ntceSpecDocUrl3?: string;
  ntceSpecDocUrl4?: string;
  ntceSpecDocUrl5?: string;
  ntceSpecDocUrl6?: string;
  ntceSpecDocUrl7?: string;
  ntceSpecDocUrl8?: string;
  ntceSpecDocUrl9?: string;
  ntceSpecDocUrl10?: string;
  ntceSpecFileNm1?: string;
  ntceSpecFileNm2?: string;
  ntceSpecFileNm3?: string;
  ntceSpecFileNm4?: string;
  ntceSpecFileNm5?: string;
  ntceSpecFileNm6?: string;
  ntceSpecFileNm7?: string;
  ntceSpecFileNm8?: string;
  ntceSpecFileNm9?: string;
  ntceSpecFileNm10?: string;
  /* 아래는 원본(raw)에 늘 있었지만 화면이 안 쓰던 것 — 상세에서 보인다(2026-09-30) */
  /** 낙찰방법 — 「제안적격자 중 …」 · 「협상에 의한 계약」이면 제안서가 필요하다 */
  sucsfbidMthdNm?: string;
  /** 변경공고일 때 사유 */
  chgNtceRsn?: string;
  /** 공동수급 — 「(없음)공동수급불허」 등 */
  cmmnSpldmdMethdNm?: string;
  /** 입찰참가자격 등록 마감 — 이 뒤엔 등록을 못 해 입찰도 못 한다 */
  bidQlfctRgstDt?: string;
  bidBeginDt?: string;
  opengPlce?: string;
  /** 세부 분류 — 대 › 중 › 이것 */
  pubPrcrmntClsfcNm?: string;
  /** 예정가격 결정 방법 — 복수예가 등 */
  prearngPrceDcsnMthdNm?: string;
  VAT?: string;
  /** 참가 지역 제한 */
  prtcptLmtRgnNm?: string;
  ntceInsttOfclNm?: string;
  ntceInsttOfclTelNo?: string;
  ntceInsttOfclEmailAdrs?: string;
  /** 사전규격 번호 — 본공고 전에 규격을 미리 공개한 건 */
  bfSpecRgstNo?: string;
  /** 표준 공고문(나라장터가 만든 공고문 파일) */
  stdNtceDocUrl?: string;
}

/** 제안서를 내야 하는 낙찰방법인가 — 컨설팅 · 연구용역이 여기 몰린다 */
const needsProposal = (m?: string) => !!m && /제안|협상/.test(m);

/** 나라장터는 차수마다 같은 면허를 다시 준다 — 이름 · 대체 면허가 같으면 하나로 */
function uniqueLicenses(list: NonNullable<BidItem['licenses']>) {
  const seen = new Set<string>();
  return list.filter((l) => {
    const k = `${l.lcnsLmtNm}|${l.permsnIndstrytyList ?? ''}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

interface SearchResult {
  items: BidItem[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * 이미 프로젝트로 들인 공고. `GET /bidding/captured` 의 한 줄.
 *
 * 단계까지 받는 이유 — 북마크는 아직 「담을 수 있는」 상태다. 담김과 같이 칠하면
 * 나중에 하기로 마음먹었을 때 올릴 방법이 화면에 없다.
 */
interface CapturedBid {
  id: number;
  key: string;
  stage: '북마크' | '검토' | '재무계획' | '제안서' | '수행' | '종료';
  outcome: '수주' | '실주' | '미참여' | null;
}

function formatPrice(value: string): string {
  const num = parseInt(value, 10);
  if (isNaN(num) || num === 0) return '-';
  return num.toLocaleString('ko-KR') + '원';
}

function formatDateTime(dt: string): string {
  if (!dt) return '-';
  // 나라장터는 「202610131000」 또는 「2026-10-13 10:00:00」 둘 다 준다 — 초는 뗀다(#113)
  const s = dt.replace(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/, '$1-$2-$3 $4:$5');
  return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s) ? s.slice(0, 16) : s;
}

/** 마감까지 남은 날. 지났거나 값이 없으면 null */
/**
 * 마감으로 볼 시각. 마감일시가 비면 개찰일시를 쓴다.
 *
 * 협상에 의한 계약·제안서 평가형은 입찰서 마감 대신 개찰일시를 쓴다.
 * 표본 433건 중 70건(16%)이 그랬고 전부 개찰일시가 있었다 —
 * 컨설팅·학술용역이 여기 몰려 있다. BE 도 같은 규칙으로 거르고 정렬한다.
 */
function closingAt(item: Pick<BidItem, 'bidClseDt' | 'opengDt'>): string {
  return item.bidClseDt || item.opengDt || '';
}

/** 화면에 「마감」이라 쓸지 「개찰」이라 쓸지 */
function closingLabel(item: Pick<BidItem, 'bidClseDt' | 'opengDt'>): string {
  return item.bidClseDt ? '마감' : '개찰';
}

function daysLeft(bidClseDt: string): number | null {
  if (!bidClseDt) return null;
  const close = new Date(bidClseDt.replace(/\//g, '-'));
  if (isNaN(close.getTime())) return null;
  const diff = close.getTime() - Date.now();
  if (diff < 0) return null;
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

/** 금액을 짧게 — 3.2억 · 4,500만 */
function shortPrice(value?: string): string {
  const n = parseInt(value ?? '', 10);
  if (!n) return '';
  if (n >= 1e8) return `${(n / 1e8).toFixed(n >= 1e9 ? 0 : 1).replace(/\.0$/, '')}억`;
  return `${Math.round(n / 1e4).toLocaleString('ko-KR')}만`;
}

/** 한눈 요약 — 공고에 이미 있는 값으로 만든다. 지원할지 가늠하는 순서로 */
function quickFacts(b: BidItem, licenseCount: number): string[] {
  const at = closingAt(b);
  const d = daysLeft(at);
  const price = shortPrice(b.presmptPrce) || shortPrice(b.asignBdgtAmt);
  return [
    d != null ? `${closingLabel(b)} ${d <= 1 ? '임박' : `D-${d}`}` : '',
    price ? `추정 ${price}` : '',
    needsProposal(b.sucsfbidMthdNm) ? '제안서 평가' : b.sucsfbidMthdNm ? '가격 경쟁' : '',
    b.cntrctCnclsMthdNm ?? '',
    // 목록 API 는 지역 제한을 거의 비워 준다(10,532건 전부 빈 값, 2026-09-30) — 없다고 단정하지 않는다
    b.prtcptLmtRgnNm ? `${b.prtcptLmtRgnNm} 제한` : '',
    b.cmmnSpldmdMethdNm ? (/불허|없음/.test(b.cmmnSpldmdMethdNm) ? '공동수급 불가' : '공동수급 가능') : '',
    // 원본이 지워진 북마크는 면허를 모른다 — 「제한 없음」으로 단정하지 않는다
    licenseCount ? `면허 ${licenseCount}개` : (b as any)._snapshotOnly ? '' : '면허 제한 없음',
  ].filter(Boolean);
}

function isClosingSoon(bidClseDt: string): boolean {
  if (!bidClseDt) return false;
  const close = new Date(bidClseDt.replace(/\//g, '-'));
  const diff = close.getTime() - Date.now();
  return diff > 0 && diff < 24 * 60 * 60 * 1000; // 24시간 이내
}

/* 게시일은 거르는 조건이 아니라 「새로 올라온 것만」 볼 때 쓴다. 목록은 이미
   마감 전 공고만이라 기본은 전체다 — 7일로 두면 지원 가능한 공고의 44%가
   안 보였다(2026-09-30). 31일 제한은 나라장터 API 를 직접 부를 때의 것이라
   DB 를 보는 지금은 없다. */
const PRESETS = [
  { label: '전체', value: 'all' },
  { label: '최근 3일', value: '3' },
  { label: '최근 7일', value: '7' },
  { label: '최근 14일', value: '14' },
  { label: '직접 고르기', value: 'custom' },
] as const;
type Preset = (typeof PRESETS)[number]['value'];

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 공고 한 줄의 담기 버튼.
 *
 * ```
 * 아무것도 아님   ☆ + 프로젝트로     북마크하거나 바로 담는다
 * 북마크          ★ + 프로젝트로     아직 담을 수 있다
 * 검토 이후       단계 배지          여기서 되돌리지 않는다
 * 미참여          미참여 배지        뺀 것을 다시 담지 않게
 * ```
 */
/** 공고 조회 조건. 저장된 수집 기준을 불러올 때 한 번에 넘긴다 */
interface Filters {
  keyword: string;
  /** 검색칸 — 공고명 또는 기관명 중 하나를 찾는다 */
  searchField: 'name' | 'inst';
  searchText: string;
  excludeKeyword: string;
  minPrice: string;
  maxPrice: string;
  bizDiv: '용역' | '물품' | '공사';
  classification: string;
}

const BidActions: React.FC<{
  item: BidItem;
  captured?: CapturedBid;
  busy: boolean;
  onCapture: (item: BidItem, how: 'bookmark' | 'take') => void;
}> = ({ item, captured, busy, onCapture }) => {
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation(); // 행을 누르면 상세가 열린다 — 버튼은 거기까지 가지 않는다
    fn();
  };

  // 미참여로 뺀 것도 다시 담을 수 있다(#108) — 북마크로 되살린다
  if (captured?.outcome === '미참여') {
    return (
      <div className="flex items-center gap-1.5">
        <span className="whitespace-nowrap rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-500">미참여</span>
        <ConfirmButton
          disabled={busy}
          onConfirm={() => onCapture(item, 'bookmark')}
          title="미참여를 풀고 북마크로 되살립니다"
          ask="한 번 더 — 북마크로"
          armedClassName="whitespace-nowrap rounded-lg px-2 py-1.5 text-xs font-medium bg-amber-500 text-white"
          className="whitespace-nowrap rounded-lg px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100"
        >
          다시 담기
        </ConfirmButton>
      </div>
    );
  }

  // 검토부터는 사람이 이미 일을 시작한 것이다. 공고 화면에서 손대지 않고, 그 프로젝트로 가는 길만 준다(#111)
  if (captured && captured.stage !== '북마크') {
    return (
      <Link
        to={`/projects/${captured.id}`}
        onClick={(e) => e.stopPropagation()}
        title="프로젝트 열기"
        className="whitespace-nowrap rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
      >
        {captured.stage} →
      </Link>
    );
  }

  const bookmarked = captured?.stage === '북마크';

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        disabled={busy || bookmarked}
        onClick={stop(() => onCapture(item, 'bookmark'))}
        title={bookmarked ? '북마크됨 — 팀 전체가 봅니다' : '북마크 (나중에 볼 것)'}
        className={`rounded-lg border p-1.5 disabled:cursor-default ${
          bookmarked
            ? 'border-amber-200 bg-amber-50 text-amber-500'
            : 'border-slate-300 text-slate-400 hover:bg-slate-50 hover:text-amber-500'
        }`}
      >
        <Bookmark size={14} fill={bookmarked ? 'currentColor' : 'none'} />
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={stop(() => onCapture(item, 'take'))}
        className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
      >
        {busy ? '담는 중...' : '프로젝트로'}
      </button>
    </div>
  );
};

/** 조회 줄의 작은 칸 */
const SEL = 'rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500';

export const BiddingPage: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [searchField, setSearchField] = useState<'name' | 'inst'>('name');
  const [searchText, setSearchText] = useState('');
  const institution = searchField === 'inst' ? searchText : '';
  const [beginDate, setBeginDate] = useState(daysAgoStr(7));
  const [endDate, setEndDate] = useState(todayStr());
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [selectedBid, setSelectedBid] = useState<BidItem | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('bidClseDt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // 업무구분은 하나만. 나라장터가 엔드포인트로 나눠서, 여럿이면 왕복이 그만큼 는다
  /* 공고를 가져오는 곳. 지금은 나라장터뿐이라 고를 것이 하나지만, 자리를
     만들어 둔다 — NIPA 등이 붙을 때 화면을 다시 고치지 않아도 된다.
     출처마다 업무구분·분류 체계가 다르므로 여기가 가장 앞선 선택이다. */
  const [source] = useState<BidSource>('나라장터');
  const [preset, setPreset] = useState<Preset>('all');
  const [bizDiv, setBizDiv] = useState<'용역' | '물품' | '공사'>('용역');
  const [classification, setClassification] = useState('');
  /** 상세 조건(키워드 칩 · 추정가격). 기본은 접어 둔다 — 목록이 위로 오게 */
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [excludeKeyword, setExcludeKeyword] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  /** 이미 들인 공고. 키는 `출처:공고번호` — 출처가 늘면 번호가 겹친다 */
  const [captured, setCaptured] = useState<Record<string, CapturedBid>>({});
  const [adding, setAdding] = useState<string | null>(null);
  /** 「공고」 전체 목록인가, 팀이 북마크한 것만인가 */
  const [view, setView] = useState<'all' | 'bookmarks'>('all');

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'custom' || p === 'all') return; // 직접 고르기는 지금 날짜를 그대로 두고 칸만 연다
    setBeginDate(daysAgoStr(Number(p)));
    setEndDate(todayStr());
  };

  const pageSize = 20;

  /** 접어 뒀을 때 몇 개가 걸려 있는지. 안 보이는 조건이 결과를 줄이면 헷갈린다 */
  const advancedCount =
    `${keyword},${excludeKeyword}`.split(/[,+]/).filter((w) => w.trim()).length +
    [minPrice, maxPrice].filter((v) => v.trim() !== '').length;

  /** 들인 공고 목록을 다시 읽는다. 팀 공용이라 남이 담은 것도 여기서 보인다 */
  const reloadCaptured = useCallback(async () => {
    const rows: CapturedBid[] = await apiClient.get('/bidding/captured');
    setCaptured(Object.fromEntries(rows.map((r) => [r.key, r])));
  }, []);

  useEffect(() => {
    scheduleApi.list().then(setSchedules).catch(() => {});
    reloadCaptured().catch(() => {});
  }, [reloadCaptured]);

  /**
   * 북마크·담기. **베끼는 것은 BE 가 한다** — 공고 원본이 DB 에 있으므로
   * 화면이 스냅샷을 만들어 보내면 둘이 어긋난다(예전에는 그랬다).
   */
  const capture = async (item: BidItem, how: 'bookmark' | 'take') => {
    setAdding(item.bidNtceNo);
    setError('');
    try {
      await apiClient.post(`/bidding/${encodeURIComponent(item.bidNtceNo)}/${how}`, {});
      await reloadCaptured();
    } catch (err: any) {
      setError(err.message || '담는 중 오류가 발생했습니다.');
    } finally {
      setAdding(null);
    }
  };

  /**
   * 공고 조회. 조회 조건은 state 에서 읽되, `override` 로 덮어쓸 수 있다.
   *
   * 수집 기준 칩을 누를 때 setState 가 아직 반영되기 전이라 state 를 그대로
   * 읽으면 이전 조건으로 조회된다. 그래서 전에는 이 함수를 통째로 복사해
   * 따로 갖고 있었는데, 한쪽만 고치면 다른 쪽이 조용히 어긋난다.
   */
  const fetchBids = useCallback(async (
    p: number = 1,
    override?: Partial<Filters>,
  ) => {
    const f: Filters = {
      keyword, searchField, searchText, excludeKeyword, minPrice, maxPrice, bizDiv, classification,
      ...override,
    };
    const kw = f.keyword;
    const text = f.searchText.trim();

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (preset !== 'all') {
        params.set('beginDate', beginDate);
        params.set('endDate', endDate);
      }
      params.set('page', String(p));
      params.set('pageSize', String(pageSize));
      if (kw.trim()) params.set('keyword', kw.trim());
      if (text) params.set(f.searchField === 'inst' ? 'institution' : 'name', text);
      if (f.excludeKeyword.trim()) params.set('excludeKeyword', f.excludeKeyword.trim());
      if (f.minPrice.trim()) params.set('minPrice', f.minPrice.trim());
      if (f.maxPrice.trim()) params.set('maxPrice', f.maxPrice.trim());
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      params.set('source', source);
      params.set('bizDiv', f.bizDiv);
      // 분류는 용역에만 있다 — 다른 구분에서 넘기면 0건이 된다
      if (f.bizDiv === '용역' && f.classification) params.set('classification', f.classification);

      const data = await apiClient.get(`/bidding/search?${params.toString()}`);
      setResult(data);
      setPage(p);
    } catch (err: any) {
      setError(err.message || '조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [preset, beginDate, endDate, keyword, searchField, searchText, excludeKeyword, minPrice, maxPrice, sortBy, sortOrder, source, bizDiv, classification]);

  // 최초 1회만. fetchBids 는 필터값에 의존하므로 의존성에 넣으면
  // 필터가 바뀔 때마다 재조회가 돌아 아래 useEffect 와 겹친다.
  useEffect(() => {
    fetchBids(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 정렬이 바뀔 때만. result 를 의존성에 넣으면 조회 결과가 바뀔 때마다
  // 다시 조회해 무한 루프가 된다.
  useEffect(() => {
    if (result) fetchBids(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchBids(1);
  };

  /* 저장된 기준의 조건을 전부 채운다. 예전에는 키워드·기관만 채워서
     알림으로 오는 목록과 화면에서 보는 목록이 달랐다(2026-09-28 발견).
     면허는 조회 조건에서 뺐다(#150) — 저장된 기준에 면허가 있으면 알림과 화면이 다를 수 있다 */
  const applySchedule = (schedule: Schedule) => {
    const bd = schedule.bizDiv;
    const f: Filters = {
      keyword: schedule.keywords || '',
      searchField: schedule.institution ? 'inst' : 'name',
      searchText: schedule.institution || '',
      excludeKeyword: schedule.excludeKeywords || '',
      minPrice: schedule.minPrice != null ? String(schedule.minPrice) : '',
      maxPrice: schedule.maxPrice != null ? String(schedule.maxPrice) : '',
      bizDiv: bd === '물품' || bd === '공사' ? bd : '용역',
      classification: schedule.classification || '',
    };
    setKeyword(f.keyword);
    setSearchField(f.searchField);
    setSearchText(f.searchText);
    setExcludeKeyword(f.excludeKeyword);
    setMinPrice(f.minPrice);
    setMaxPrice(f.maxPrice);
    setBizDiv(f.bizDiv);
    setClassification(f.classification);
    if (f.keyword || f.excludeKeyword || f.minPrice || f.maxPrice) setShowAdvanced(true);
    // state 반영을 기다리지 않고 값을 직접 넘긴다
    fetchBids(1, f);
  };

  // 북마크 탭이나 상세 패널이 열린 채로 누르면 짚을 곳이 없다 — 전체 공고로 돌려놓고 시작한다


  const totalPages = result ? Math.ceil(result.total / pageSize) : 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <h1 className="text-xl font-bold text-slate-900 mb-5 flex items-center gap-1.5">
        공고
      </h1>

      {/* 탭 줄 오른쪽에 수집 기준 — 조회 영역을 한 줄로 줄여 목록이 위로 오게(#150) */}
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-200">
        {([
          ['all', '전체 공고'],
          ['bookmarks', `북마크 ${Object.values(captured).filter((c) => c.stage === '북마크' && !c.outcome).length}`],
        ] as const).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              view === v ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
        {view === 'all' && (
          <div className="ml-auto flex flex-wrap items-center gap-1.5 pb-1">
            {schedules.length > 0 && <span className="text-xs text-slate-400">수집 기준</span>}
            {schedules.map((s) => (
              <button
                key={s.id}
                onClick={() => applySchedule(s)}
                className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-600 hover:bg-indigo-100"
              >
                {s.name}
              </button>
            ))}
            <button
              onClick={() => setScheduleOpen(true)}
              title="지금 조건으로 수집 기준 만들기"
              className="rounded-full border border-slate-300 px-2.5 py-0.5 text-xs text-slate-500 hover:bg-slate-50"
            >
              + 저장
            </button>
          </div>
        )}
      </div>

      {view === 'bookmarks' ? (
        <BidBookmarkList
          onOpen={(bid) => setSelectedBid(bid as unknown as BidItem)}
          onChanged={() => reloadCaptured().catch(() => {})}
        />
      ) : (<>
      <ScheduleFormModal
        open={scheduleOpen}
        initial={{
          keywords: keyword, institution, excludeKeywords: excludeKeyword, bizDiv, classification,
          minPrice: minPrice ? Number(minPrice) : null, maxPrice: maxPrice ? Number(maxPrice) : null,
        }}
        onClose={() => setScheduleOpen(false)}
        onSaved={() => { setScheduleOpen(false); scheduleApi.list().then(setSchedules).catch(() => {}); }}
      />

      {/* 조회 조건 — 한 줄. 키워드 칩 · 추정가격은 「상세」로 접는다 */}
      <form onSubmit={handleSearch} className="mb-4 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="업무구분"
            value={bizDiv}
            onChange={(e) => {
              const v = e.target.value as '용역' | '물품' | '공사';
              setBizDiv(v);
              if (v !== '용역') setClassification(''); // 분류는 용역에만 있다
            }}
            className={SEL}
          >
            <option value="용역">용역</option>
            <option value="물품">물품</option>
            <option value="공사">공사</option>
          </select>
          <select
            aria-label="분류"
            value={classification}
            disabled={bizDiv !== '용역'}
            onChange={(e) => setClassification(e.target.value)}
            title={bizDiv !== '용역' ? '분류는 용역 공고에만 있습니다' : undefined}
            className={`${SEL} max-w-[11rem] disabled:bg-slate-50 disabled:text-slate-300`}
          >
            <option value="">분류 전체</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select aria-label="게시일" value={preset} onChange={(e) => applyPreset(e.target.value as Preset)} className={SEL}>
            {PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.value === 'all' ? '게시일 전체' : p.label}</option>
            ))}
          </select>
          {preset === 'custom' && (
            <>
              <input type="date" aria-label="시작일" value={beginDate} max={endDate} onChange={(e) => setBeginDate(e.target.value)} className={SEL} />
              <input type="date" aria-label="종료일" value={endDate} min={beginDate} max={todayStr()} onChange={(e) => setEndDate(e.target.value)} className={SEL} />
            </>
          )}

          <div className="flex min-w-[16rem] flex-1 overflow-hidden rounded-lg border border-slate-300 focus-within:ring-2 focus-within:ring-indigo-500">
            <select
              aria-label="찾을 곳"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value as 'name' | 'inst')}
              className="border-0 border-r border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-700 focus:outline-none"
            >
              <option value="name">공고명</option>
              <option value="inst">기관명</option>
            </select>
            <input
              type="text"
              aria-label="검색어"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={searchField === 'inst' ? '공고 · 수요기관' : '공고명 일부'}
              className="min-w-0 flex-1 border-0 px-2.5 py-1.5 text-sm focus:outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? '조회 중...' : '검색'}
          </button>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            aria-expanded={showAdvanced}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 ${showAdvanced ? 'bg-indigo-50' : ''}`}
          >
            상세
            {advancedCount > 0 && (
              <span className="rounded-full bg-indigo-600 px-1.5 text-[11px] text-white">{advancedCount}</span>
            )}
            <ChevronDown size={14} className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showAdvanced && (
          <div className="mt-2.5 space-y-2 border-t border-slate-100 pt-2.5">
            <KeywordFields
              value={{ keyword, exclude: excludeKeyword }}
              onChange={(v) => {
                setKeyword(v.keyword);
                setExcludeKeyword(v.exclude);
              }}
            />
            <div className="flex items-center gap-3">
              <span className="w-14 flex-shrink-0 text-xs font-medium text-slate-500">추정가격</span>
              <input type="number" min={0} aria-label="추정가격 최소" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="최소" className={`${SEL} w-32`} />
              <span className="text-slate-400">~</span>
              <input type="number" min={0} aria-label="추정가격 최대" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="최대" className={`${SEL} w-32`} />
            </div>
          </div>
        )}
      </form>

      {/* 에러 */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* 결과 헤더 + 정렬 */}
      {result && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            총 <span className="font-semibold text-slate-900">{result.total.toLocaleString()}</span>건
          </p>
          {/* 정렬 — 시안의 세 가지 */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {SORTS.map((s) => {
              const on = sortBy === s.sortBy && sortOrder === s.sortOrder;
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => {
                    setSortBy(s.sortBy);
                    setSortOrder(s.sortOrder);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    on ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 로딩 */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-indigo-500 mr-2" />
          <span className="text-slate-500">나라장터 공고를 조회하고 있습니다...</span>
        </div>
      )}

      {/* 결과 목록 — 시안의 표 형태. 고밀도 데이터 테이블이 내부 도구 기준이다 */}
      {!loading && result && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          {result.items.length === 0 ? (
            <div className="text-center py-20 text-slate-400 text-sm">
              검색 결과가 없습니다.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500">
                  <th className="pl-4 pr-1 py-3 w-12 text-right">No</th>
                  <th className="px-4 py-3">공고명</th>
                  <th className="px-4 py-3 w-40">발주기관</th>
                  <th className="px-4 py-3 w-32 text-right">추정가격</th>
                  <th className="px-4 py-3 w-36">마감</th>
                  <th className="px-4 py-3 w-32">검토</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.items.map((item, idx) => {
                  const at = closingAt(item);
                  const closing = isClosingSoon(at);
                  const dday = daysLeft(at);
                  return (
                    <tr
                      key={`${item.bidNtceNo}-${item.bidNtceOrd}`}
                      onClick={() => setSelectedBid(item)}
                      className="hover:bg-slate-50 cursor-pointer align-top"
                    >
                      <td className="pl-4 pr-1 py-3 text-right text-xs tabular-nums text-slate-400">
                        {(page - 1) * pageSize + idx + 1}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 line-clamp-2">
                          {item.bidNtceNm}
                        </div>
                        <div className="mt-1 text-xs text-slate-400">
                          {[
                            item.ntceKindNm,
                            item.cntrctCnclsMthdNm,
                            // 몇 번 고쳐진 공고인지. 000 은 한 번도 안 고친 것이라 안 적는다
                            item.bidNtceOrd && item.bidNtceOrd !== '000'
                              ? `정정 ${Number(item.bidNtceOrd)}차`
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' · ') || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {item.ntceInsttNm || '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900 whitespace-nowrap">
                        {formatPrice(item.presmptPrce)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {dday !== null && (
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-semibold ${
                              closing ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            D-{dday}
                          </span>
                        )}
                        <div className="mt-1 text-xs text-slate-400">
                          {at ? `${closingLabel(item)} ${formatDateTime(at)}` : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <BidActions
                          item={item}
                          captured={captured[`${source}:${item.bidNtceNo}`]}
                          busy={adding === item.bidNtceNo}
                          onCapture={capture}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 페이지네이션 */}
      {!loading && result && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => fetchBids(page - 1)}
            disabled={page <= 1}
            className="p-2 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-slate-600 px-3">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => fetchBids(page + 1)}
            disabled={page >= totalPages}
            className="p-2 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      </>)}

      {/* 상세 — 오른쪽에서 밀려 나오는 패널(admin 과 같은 모양, 2026-10-01).
          머리(공고명) · 본문(스크롤) · 아래(나라장터 버튼)는 SidePanel 이 나눈다 */}
      {selectedBid && (() => {
        const b = selectedBid;
        const closeAt = closingAt(b);
        const closed = !!closeAt && new Date(closeAt.replace(' ', 'T')) < new Date();
        const licenses = uniqueLicenses(b.licenses ?? []);
        const officer = [b.ntceInsttOfclNm, b.ntceInsttOfclTelNo, b.ntceInsttOfclEmailAdrs].filter(Boolean).join(' · ');
        const attachments = ATTACH_SLOTS
          .map((i) => ({ url: (b as any)[`ntceSpecDocUrl${i}`] as string | undefined, name: (b as any)[`ntceSpecFileNm${i}`] as string | undefined, i }))
          .filter((x) => x.url);
        return (
        <SidePanel
          key={b.bidNtceNo}
          onClose={() => setSelectedBid(null)}
          header={
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 text-xs mb-1.5">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{b.bidNtceNo}{b.bidNtceOrd ? `-${b.bidNtceOrd}` : ''}</span>
                  {b.ntceKindNm && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{b.ntceKindNm}</span>}
                  {needsProposal(b.sucsfbidMthdNm) && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium">제안서 필요</span>}
                  {closed && <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">마감 지남</span>}
                </div>
                <h2 className="text-base font-bold text-slate-900 leading-snug">{b.bidNtceNm}</h2>
              </div>
          }
          footer={
              <>
              {/* 요약을 읽고 바로 담는다 — 전엔 패널을 닫고 목록에서 그 줄을 다시 찾아야 했다(#111) */}
              <div className="mr-auto">
                {(b as any)._snapshotOnly ? (
                  // 마감돼 공고 원본이 지워진 북마크 — 담을 수 없다
                  <span className="text-xs text-slate-400">마감된 공고 · 담을 때 저장한 내용</span>
                ) : (
                  <BidActions item={b} captured={captured[`${source}:${b.bidNtceNo}`]} busy={adding === b.bidNtceNo} onCapture={capture} />
                )}
              </div>
              <a
                href={b.bidNtceDtlUrl || b.bidNtceUrl || `https://www.g2b.go.kr`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
              >
                <ExternalLink size={16} />
                나라장터에서 보기
              </a>
              </>
          }
        >
            <HideEmptyRows.Provider value={!!(b as any)._snapshotOnly}>
            <div className="space-y-5">
              {/* 담기 · 북마크 실패를 패널 안에서도 — 목록 맨 위에만 떠서 안 보였다(#111) */}
              <ErrorBanner message={error || null} onClose={() => setError('')} />
              <BidSummaryPanel bidNtceNo={b.bidNtceNo} facts={quickFacts(b, licenses.length)} readOnly={!!(b as any)._snapshotOnly} />

              {b.ntceKindNm === '변경공고' && b.chgNtceRsn && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">변경 사유 — {b.chgNtceRsn}</p>
              )}

              <DetailSection title="일정">
                <DetailRow label="공고일시" value={formatDateTime(b.bidNtceDt)} />
                {b.bidBeginDt && <DetailRow label="입찰 시작" value={formatDateTime(b.bidBeginDt)} />}
                {b.bidQlfctRgstDt && <DetailRow label="참가자격 등록 마감" value={formatDateTime(b.bidQlfctRgstDt)} />}
                {/* 마감이 없는 공고(협상 · 제안)는 개찰이 마감 역할이다 — 붉은 「-」 대신 그렇게 적는다(#113) */}
                <DetailRow label="마감일시" value={b.bidClseDt ? formatDateTime(b.bidClseDt) : b.opengDt ? '없음 — 개찰일시가 마감' : '-'} highlight={!!b.bidClseDt} />
                {/* 개찰은 마감 뒤에 연다. 공고가 DB 에서 지워지는 기준이기도 하다 */}
                {b.opengDt && <DetailRow label="개찰일시" value={`${formatDateTime(b.opengDt)}${b.opengPlce && !b.opengPlce.includes('나라장터') ? ` · ${b.opengPlce}` : ''}`} />}
              </DetailSection>

              <DetailSection title="금액">
                <DetailRow label="추정가격" value={formatPrice(b.presmptPrce)} />
                <DetailRow label="배정예산" value={formatPrice(b.asignBdgtAmt)} />
                {b.prearngPrceDcsnMthdNm && <DetailRow label="예정가격" value={b.prearngPrceDcsnMthdNm} />}
              </DetailSection>

              <DetailSection title="입찰 조건">
                {b.sucsfbidMthdNm && <DetailRow label="낙찰방법" value={b.sucsfbidMthdNm} />}
                <DetailRow label="계약방법" value={b.cntrctCnclsMthdNm} />
                <DetailRow label="입찰방식" value={b.bidMethdNm} />
                {b.cmmnSpldmdMethdNm && <DetailRow label="공동수급" value={b.cmmnSpldmdMethdNm.replace(/^\(없음\)/, '')} />}
                {b.prtcptLmtRgnNm && <DetailRow label="지역 제한" value={b.prtcptLmtRgnNm} />}
                {/* 아래는 업무구분마다 있는 것이 다르다 — 없는 칸은 그리지 않는다 */}
                {b.srvceDivNm && <DetailRow label="용역구분" value={b.srvceDivNm} />}
                {b.pubPrcrmntLrgClsfcNm && (
                  <DetailRow label="분류" value={[b.pubPrcrmntLrgClsfcNm, b.pubPrcrmntMidClsfcNm, b.pubPrcrmntClsfcNm].map((x) => x?.trim()).filter(Boolean).join(' › ')} />
                )}
                {b.dtilPrdctClsfcNoNm && <DetailRow label="품명" value={b.dtilPrdctClsfcNoNm} />}
                {b.prdctQty && <DetailRow label="수량" value={`${b.prdctQty}${b.prdctUnit || ''}`} />}
              </DetailSection>

              <DetailSection title="기관">
                <DetailRow label="공고기관" value={b.ntceInsttNm} />
                {b.dminsttNm !== b.ntceInsttNm && <DetailRow label="수요기관" value={b.dminsttNm} />}
                {officer && <DetailRow label="담당자" value={officer} />}
                {b.bfSpecRgstNo && <DetailRow label="사전규격" value={b.bfSpecRgstNo} />}
              </DetailSection>

              {/* 면허제한 — 입찰에 참가하려면 이 면허가 있어야 한다.
                  물품·공사에는 대분류가 없어서 이게 「무슨 일인지」를 가장 잘 알려준다. */}
              {licenses.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <BadgeCheck size={14} /> 참가 면허
                  </h4>
                  <div className="space-y-1.5">
                    {licenses.map((l, i) => (
                      <div key={i} className="text-sm">
                        <span className="text-slate-900">{l.lcnsLmtNm}</span>
                        {l.permsnIndstrytyList && (
                          // 이 면허 대신 인정되는 것들. 우리가 낼 수 있는지 판단하는 정보다
                          <div className="text-xs text-slate-500 mt-0.5">또는 {l.permsnIndstrytyList}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(attachments.length > 0 || b.stdNtceDocUrl) && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <FileText size={14} /> 첨부문서
                  </h4>
                  <div className="space-y-1">
                    {attachments.map((a) => (
                      <a key={a.i} href={a.url} target="_blank" rel="noopener noreferrer"
                        className="block text-sm text-indigo-600 hover:underline truncate">
                        {a.name || `첨부문서 ${a.i}`}
                      </a>
                    ))}
                    {b.stdNtceDocUrl && (
                      <a href={b.stdNtceDocUrl} target="_blank" rel="noopener noreferrer"
                        className="block text-sm text-indigo-600 hover:underline truncate">
                        표준 공고문 (나라장터)
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
            </HideEmptyRows.Provider>
        </SidePanel>
        );
      })()}


    </div>
  );
};

/** 마감돼 원본이 지워진 북마크는 담을 때 저장한 값만 있다 — 빈 줄을 「-」로 늘어놓지 않는다 */
const HideEmptyRows = React.createContext(false);

const DetailRow: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => {
  const hideEmpty = React.useContext(HideEmptyRows);
  if (hideEmpty && (!value || value === '-')) return null;
  return (
    <tr className="border-b border-slate-50 last:border-0">
      <th className="py-1.5 pr-4 text-left text-slate-500 font-medium w-32 align-top whitespace-nowrap">{label}</th>
      <td className={`py-1.5 break-words ${highlight ? 'text-red-600 font-medium' : 'text-slate-900'}`}>{value || '-'}</td>
    </tr>
  );
};

/** 상세의 한 묶음 — 일정 · 금액 · 입찰 조건 · 기관 */
const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  // 줄이 다 감춰지면 제목도 감춘다(지워진 공고)
  <div className="[&:not(:has(tr))]:hidden">
    <h4 className="text-xs font-semibold text-slate-400 mb-1">{title}</h4>
    <table className="w-full text-sm"><tbody>{children}</tbody></table>
  </div>
);
